import { useCallback, useState } from 'react';
import { IClient, IInventoryEntry, IProduct } from '../../../shared/interfaces';
import { useToast } from '../../../hooks/toast';
import {
  FormOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { AInput, AModal, ASelect } from '../../../components';
import { Col, Form, Row, Card, Space, Typography } from 'antd';
import { schemaInventoryEntry } from '../schemas/yup.inventory-entry.schema';
import api from '../../../services/api';
import { currency } from '../../../shared/utils/functions';

const { Text } = Typography;

interface IInventoryEntryProps {
  open: boolean;
  close: () => void;
  setEntries: React.Dispatch<React.SetStateAction<IInventoryEntry[]>>;
  products: IProduct[];
  suppliers: IClient[];
}

const InventoryEntryCreate: React.FC<IInventoryEntryProps> = ({
  open,
  close,
  setEntries,
  products,
  suppliers,
}) => {
  const { addToast } = useToast();
  const [date, setDate] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [supplierId, setSupplierId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(0);
  const [value, setValue] = useState<number>(0);
  const [observation, setObservation] = useState<string>('');

  const handleCreate = useCallback(() => {
    schemaInventoryEntry
      .validate({
        date,
        product_id: productId,
        supplier_id: supplierId,
        quantity,
        value,
        observation,
      })
      .then(async (validated) => {
        await api
          .post(`/inventory-entries/`, validated)
          .then((response) => {
            setEntries((state: IInventoryEntry[]) => [...state, response.data]);
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: 'Entrada registrada com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.message ?? 'Erro ao cadastrar',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      });
  }, [
    date,
    productId,
    supplierId,
    value,
    quantity,
    observation,
    setEntries,
    close,
    addToast,
  ]);

  const selectedProduct = products.find((p) => p.id === productId);

  return (
    <AModal
      open={open}
      close={close}
      title={
        <>
          <FormOutlined /> Registrar Reposição no Estoque
        </>
      }
      onOk={handleCreate}
      width={620}
    >
      <Form layout="vertical" style={{ maxWidth: '100%' }}>
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row>
            <Col span={24} style={{ marginBottom: 10 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Use este cadastro quando o produto entrou na clínica e o saldo
                precisa aumentar.
              </Text>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <CalendarOutlined style={{ color: '#1890ff' }} />
                    <span>Data da entrada</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Informe a data"
                  name="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ShoppingOutlined style={{ color: '#faad14' }} />
                    <span>Fornecedor (se houver)</span>
                  </Space>
                }
              >
                <ASelect
                  showSearch
                  placeholder="Selecione"
                  optionFilterProp="children"
                  onChange={(e) => setSupplierId(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={supplierId}
                  allowClear
                  options={suppliers.map((sup) => ({
                    label: sup.corporate_name,
                    value: sup.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ShoppingOutlined style={{ color: '#52c41a' }} />
                    <span>Produto</span>
                  </Space>
                }
                required
              >
                <ASelect
                  showSearch
                  placeholder="Procure por código ou nome"
                  optionFilterProp="children"
                  onChange={(e) => setProductId(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={productId}
                  allowClear
                  options={products.map((prod) => ({
                    label: `${prod.code} - ${prod.name}`,
                    value: prod.id,
                  }))}
                />
              </Form.Item>
            </Col>

            {selectedProduct && (
              <Col span={24} style={{ marginTop: -10, marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedProduct.name} | Código: {selectedProduct.code} |
                  Unidade: {selectedProduct.unity}
                </Text>
              </Col>
            )}

            <Col xs={24} sm={12}>
              <Form.Item
                label={
                  <Space size={6}>
                    <DollarOutlined style={{ color: '#eb2f96' }} />
                    <span>Quantidade recebida</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder="Ex: 10"
                  name="quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(+e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Preço por unidade">
                <AInput
                  placeholder="Ex: 50,00"
                  name="value"
                  type="number"
                  value={value}
                  onChange={(e) => setValue(+e.target.value)}
                />
              </Form.Item>
            </Col>

            {quantity > 0 && value > 0 && (
              <Col span={24} style={{ marginBottom: 6 }}>
                <Row
                  justify="space-between"
                  style={{
                    padding: '6px 10px',
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 6,
                  }}
                >
                  <Text>Valor total:</Text>
                  <Text strong style={{ color: '#52c41a' }}>
                    {currency(quantity * value)}
                  </Text>
                </Row>
              </Col>
            )}

            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <FileTextOutlined style={{ color: '#13c2c2' }} />
                    <span>Observação (opcional)</span>
                  </Space>
                }
                style={{ marginBottom: 0 }}
              >
                <AInput
                  placeholder="Ex: compra do fornecedor, ajuste inicial, devolução"
                  name="observation"
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </AModal>
  );
};

export default InventoryEntryCreate;
